import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "@/lib/api";
import { Bell, Check, ExternalLink, Sparkles } from "lucide-react";

const POLL_INTERVAL_MS = 30000;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrap = useRef(null);

  const loadCount = async () => {
    try {
      const r = await apiGet("/notifications/unread-count");
      setUnread(r.count || 0);
    } catch (e) {}
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/notifications", { limit: 25 });
      setItems(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const markRead = async (id) => {
    try {
      await apiPost(`/notifications/${id}/read`);
      setItems((prev) =>
        prev.map((n) => (n.notification_id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      const r = await apiPost("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (e) {}
  };

  const openItem = async (n) => {
    if (!n.read_at) await markRead(n.notification_id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div ref={wrap} className="relative" data-testid="notification-bell-wrap">
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className="relative p-2 text-[#6b7280] hover:text-[#111827] transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={1.75} />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1 text-[9px] font-bold rounded-full leading-none py-[2px] px-1.5"
            data-testid="notification-badge"
            style={{ background: "#4f46e5", color: "#ffffff" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="fixed right-4 top-20 w-[400px] max-w-[calc(100vw-2rem)] max-h-[70dvh] lg:absolute lg:right-0 lg:top-full mt-2 overflow-hidden bg-white shadow-2xl z-50 flex flex-col rounded-2xl"
          style={{ boxShadow: "0 20px 60px rgba(17,24,39,0.15), 0 4px 12px rgba(17,24,39,0.08)" }}
        >
          <div className="px-6 pt-6 pb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/owlbert/main.png"
                alt="Owlbert"
                className="w-10 h-10 object-contain shrink-0"
                data-testid="notification-owlbert"
              />
              <div>
                <div className="chapter-eyebrow" style={{ fontSize: 10 }}>Notifications</div>
                <div className="text-lg font-semibold text-[#111827] mt-0.5" style={{ fontFamily: "Manrope, sans-serif" }}>Owlbert&apos;s desk</div>
              </div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all"
                className="text-[11px] font-semibold text-[#4f46e5] inline-flex items-center gap-1 hover:text-[#4338ca]"
              >
                <Check className="w-3 h-3" /> mark all
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-3">
            {loading && items.length === 0 ? (
              <div className="text-center text-xs text-[#9ca3af] py-8">listening…</div>
            ) : items.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <Sparkles className="w-5 h-5 mx-auto text-[#9ca3af] mb-3" strokeWidth={1.5} />
                <div className="text-lg font-semibold text-[#111827]" style={{ fontFamily: "Manrope, sans-serif" }}>all quiet.</div>
                <p className="text-xs text-[#6b7280] mt-1">
                  owlbert will tap your shoulder when something matters.
                </p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => openItem(n)}
                  data-testid={`notification-${n.notification_id}`}
                  className={`w-full text-left px-4 py-3 rounded-xl hover:bg-[#f9fafb] transition-colors block ${
                    !n.read_at ? "bg-[#eef2ff]/40" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`w-1.5 h-1.5 mt-2 shrink-0 ${
                        !n.read_at ? "bg-terracotta" : "bg-transparent"
                      }`}
                      style={!n.read_at ? { background: "hsl(var(--accent))" } : undefined}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="tag-chip" data-tag={tagForKind(n.kind)}>
                          {labelForKind(n.kind)}
                        </span>
                        {n.workspace_name && (
                          <span className="mono text-[10px] text-stone-dim uppercase tracking-wider truncate">
                            {n.workspace_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-parchment leading-snug">{n.title}</div>
                      {n.body && (
                        <div className="text-xs text-stone leading-relaxed mt-1 line-clamp-3">
                          {n.body}
                        </div>
                      )}
                      <div className="mono text-[10px] text-stone-dim mt-2 inline-flex items-center gap-1.5">
                        {fmtRel(n.created_at)}
                        {n.link && <ExternalLink className="w-2.5 h-2.5" />}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function labelForKind(k) {
  return (
    {
      weekly_review: "weekly review",
      hot_lead: "hot lead",
      overperforming_post: "overperformer",
      silence_warning: "silence",
      first_drafts_ready: "drafts ready",
      info: "info",
    }[k] || k
  );
}

function tagForKind(k) {
  return (
    {
      weekly_review: "performance",
      hot_lead: "pipeline",
      overperforming_post: "performance",
      silence_warning: "feedback",
      first_drafts_ready: "preference",
      info: "fact",
    }[k] || "fact"
  );
}

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}
